// src/views/credit/CustomerCreditPage.jsx
import React, { useEffect, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  getCustomerCreditService,
  getCustomerCreditItemsService,
} from '@/services/CustomerServices';
import validationErrors from '@/lib/validationErrors';

import CreditOverviewComponent from '../component/CreditOverviewComponent';
import CreditProgressRing      from '../component/CreditProgressRing';

/* helpers */
const fmtDate = (val) => {
  if (!val) return '—';
  const d = new Date(val);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleDateString();
};
const fmtAmount = (val) => {
  const n = Number(val);
  if (!Number.isFinite(n)) return '—';
  return n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

/* tab button */
const TabButton = ({ label, active, onClick }) => (
  <button
    onClick={onClick}
    className={`px-3 py-2 text-sm font-medium rounded-t-md
                ${active ? 'bg-primary text-white' : 'bg-gray-100 hover:bg-gray-200'}`}
  >
    {label}
  </button>
);

/* items table */
const CreditItemsTable = ({ items = [], isLoading }) => {
  const renderItems = (orderItems = []) => {
    if (!Array.isArray(orderItems) || orderItems.length === 0) return '—';
    if (orderItems.length <= 2) return orderItems.map(i => i?.name ?? '—').join(', ');
    const firstTwo = orderItems.slice(0, 2).map(i => i?.name ?? '—').join(', ');
    return `${firstTwo} +${orderItems.length - 2} more`;
  };

  if (isLoading)        return <p className="py-6 text-center text-gray-500">Loading…</p>;
  if (!items.length)    return <p className="py-6 text-center text-gray-500">No records found.</p>;

  return (
    <div className="overflow-x-auto mt-4">
      <table className="min-w-full text-sm">
        <thead>
          <tr className="border-b text-gray-600 whitespace-nowrap">
            <th className="py-2 pr-3">#</th>
            <th className="py-2 pr-3">Type</th>
            <th className="py-2 pr-3">Source</th>
            <th className="py-2 pr-3">Items</th>
            <th className="py-2 pr-3">Date</th>
            <th className="py-2 pr-3">Amount</th>
            <th className="py-2 pr-3">Status</th>
          </tr>
        </thead>
        <tbody>
          {(items || []).map((item, idx) => {
            const paid =
              item?.paid ??
              (typeof item?.status === 'string' && item.status.toLowerCase() === 'paid');

            return (
              <tr key={item?._id ?? idx} className="border-b last:border-0">
                <td className="py-2 pr-3">{idx + 1}</td>
                <td className="py-2 pr-3 capitalize">{item?.orderType ?? '—'}</td>
                <td className="py-2 pr-3">{item?.sourceLabel ?? '—'}</td>
                <td className="py-2 pr-3">{renderItems(item?.items)}</td>
                <td className="py-2 pr-3">{fmtDate(item?.date)}</td>
                <td className="py-2 pr-3">{fmtAmount(item?.amount)}</td>
                <td className="py-2 pr-3">
                  {paid ? (
                    <span className="text-green-600 font-semibold">Paid</span>
                  ) : (
                    <span className="text-red-600 font-semibold">Unpaid</span>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

/* main page */
const CustomerCreditPage = () => {
  const dispatch = useDispatch();
  const customer = useSelector((s) => s.Auth.userData);

  const [summary,        setSummary]       = useState({ totalCredit:0, usedCredit:0, remainingCredit:0 });
  const [summaryLoading, setSL]            = useState(false);
  const [items,          setItems]         = useState([]);
  const [itemsLoading,   setIL]            = useState(false);
  const [activeTab,      setActiveTab]     = useState('unpaid'); // 'unpaid' | 'paid' | 'all'

  useEffect(() => {
    if (!customer?._id) return;
    setSL(true);
    dispatch(getCustomerCreditService({ customerId: customer._id }))
      .then(res => {
        const d = res?.data?.data ?? {};
        setSummary({
          totalCredit    : d.totalCredit     ?? 0,
          usedCredit     : d.usedCredit      ?? 0,
          remainingCredit: d.remainingCredit ?? 0,
        });
      })
      .catch(({ message, errorData }) => validationErrors(errorData, message))
      .finally(() => setSL(false));
  }, [customer, dispatch]);

  const fetchItems = useCallback(() => {
    if (!customer?._id) return;
    setIL(true);
    const status = activeTab === 'all' ? undefined : activeTab;
    dispatch(getCustomerCreditItemsService({ customerId: customer._id, status }))
      .then(res => setItems(res?.data?.data ?? []))
      .catch(({ message, errorData }) => validationErrors(errorData, message))
      .finally(() => setIL(false));
  }, [activeTab, customer, dispatch]);

  useEffect(fetchItems, [fetchItems]);

  return (
    <div className="max-w-6xl mx-auto p-6">
      <h1 className="font-bold text-2xl mb-4">Credit Centre</h1>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
        <CreditOverviewComponent creditData={summary} isLoading={summaryLoading} />
        <CreditProgressRing used={+summary.usedCredit} limit={+summary.totalCredit} />
      </div>

      <div className="flex gap-2 border-b mb-1">
        <TabButton label="Unpaid" active={activeTab==='unpaid'} onClick={() => setActiveTab('unpaid')} />
        <TabButton label="Paid"   active={activeTab==='paid'}   onClick={() => setActiveTab('paid')}   />
        <TabButton label="All"    active={activeTab==='all'}    onClick={() => setActiveTab('all')}    />
      </div>

      <CreditItemsTable items={items} isLoading={itemsLoading} />
    </div>
  );
};

export default CustomerCreditPage;
